/**
 * AuthContext — manages user session state for the dashboard.
 *
 * Provides:
 * - Current user + organisations
 * - login(), register(), logout() actions
 * - activeOrgId + setActiveOrgId for organisation switching
 * - Loading state during auth initialization
 */

import type { ReactNode } from 'react';
import { STORAGE_KEYS } from '../lib/constants';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch, clearSessionToken, getSessionToken, setSessionToken } from '../lib/api';
import type { AuthResponse, MeResponse, Organisation, User } from '../lib/types';

interface AuthContextValue {
  user: User | null;
  organisations: Organisation[];
  activeOrgId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setActiveOrgId: (orgId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEYS.ACTIVE_ORG),
  );
  const [isLoading, setIsLoading] = useState(true);

  // Fetch current user from /v1/auth/me
  const refreshUser = useCallback(async () => {
    const token = getSessionToken();
    if (!token) {
      setUser(null);
      setOrganisations([]);
      setIsLoading(false);
      return;
    }

    try {
      const data = await apiFetch<MeResponse>('/v1/auth/me');
      setUser(data.user);
      setOrganisations(data.organisations);

      // Auto-select first org if none selected
      if (!activeOrgId && data.organisations.length > 0) {
        const firstOrgId = data.organisations[0]?.id;
        if (firstOrgId) {
          setActiveOrgIdState(firstOrgId);
          localStorage.setItem(STORAGE_KEYS.ACTIVE_ORG, firstOrgId);
        }
      }
    } catch {
      // Token invalid or expired
      clearSessionToken();
      setUser(null);
      setOrganisations([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeOrgId]);

  // Initialize on mount — check for existing token
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<AuthResponse>('/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setSessionToken(data.token);
    setUser(data.user);
    // Fetch organisations after login
    const meData = await apiFetch<MeResponse>('/v1/auth/me');
    setOrganisations(meData.organisations);
    if (meData.organisations.length > 0) {
      const firstOrgId = meData.organisations[0]?.id;
      if (firstOrgId) {
        setActiveOrgIdState(firstOrgId);
        localStorage.setItem(STORAGE_KEYS.ACTIVE_ORG, firstOrgId);
      }
    }
  }, []);

  const register = useCallback(
    async (email: string, password: string, fullName?: string) => {
      await apiFetch('/v1/auth/register', {
        method: 'POST',
        body: { email, password, fullName },
      });
      // After registration, auto-login
      await login(email, password);
    },
    [login],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch('/v1/auth/logout', { method: 'POST' });
    } catch {
      // Ignore errors — clear local state regardless
    }
    clearSessionToken();
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_ORG);
    setUser(null);
    setOrganisations([]);
    setActiveOrgIdState(null);
  }, []);

  const setActiveOrgId = useCallback((orgId: string) => {
    setActiveOrgIdState(orgId);
    localStorage.setItem(STORAGE_KEYS.ACTIVE_ORG, orgId);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      organisations,
      activeOrgId,
      isLoading,
      isAuthenticated: user !== null,
      login,
      register,
      logout,
      refreshUser,
      setActiveOrgId,
    }),
    [
      user,
      organisations,
      activeOrgId,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
      setActiveOrgId,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
